import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Plus,
  TrendingUp,
  Users,
  Building2,
  Euro,
  CalendarIcon,
  ArrowRight,
  Phone,
  Mail,
  Target,
  DollarSign,
} from 'lucide-react';

const STAGES = [
  { value: 'lead', label: 'Lead', color: 'bg-slate-100 text-slate-700' },
  { value: 'qualified', label: 'Qualifié', color: 'bg-blue-100 text-blue-700' },
  { value: 'proposal', label: 'Proposition', color: 'bg-violet-100 text-violet-700' },
  { value: 'negotiation', label: 'Négociation', color: 'bg-amber-100 text-amber-700' },
  { value: 'won', label: 'Gagné', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-700' },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
  }).format(value);
};

export default function CRMPage() {
  const [prospects, setProspects] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pipeline');
  
  // Prospect form
  const [isProspectDialogOpen, setIsProspectDialogOpen] = useState(false);
  const [prospectForm, setProspectForm] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    source: '',
    notes: '',
  });

  // Opportunity form
  const [isOpportunityDialogOpen, setIsOpportunityDialogOpen] = useState(false);
  const [opportunityForm, setOpportunityForm] = useState({
    title: '',
    prospect_id: '',
    client_id: '',
    description: '',
    estimated_value: '',
    stage: 'lead',
    probability: '20',
    expected_close_date: null,
    assigned_to: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prospectsRes, opportunitiesRes, usersRes] = await Promise.all([
        api.get('/prospects'),
        api.get('/opportunities'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setProspects(prospectsRes.data);
      setOpportunities(opportunitiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProspect = async () => {
    if (!prospectForm.company_name || !prospectForm.contact_name) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      await api.post('/prospects', prospectForm);
      toast.success('Prospect créé');
      setIsProspectDialogOpen(false);
      setProspectForm({
        company_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        source: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleConvertProspect = async (prospectId) => {
    try {
      await api.put(`/prospects/${prospectId}/convert`);
      toast.success('Prospect converti en client');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    }
  };

  const handleCreateOpportunity = async () => {
    if (!opportunityForm.title) {
      toast.error('Veuillez remplir le titre');
      return;
    }

    try {
      const payload = {
        ...opportunityForm,
        estimated_value: parseFloat(opportunityForm.estimated_value) || 0,
        probability: parseInt(opportunityForm.probability) || 0,
        expected_close_date: opportunityForm.expected_close_date?.toISOString() || null,
      };
      await api.post('/opportunities', payload);
      toast.success('Opportunité créée');
      setIsOpportunityDialogOpen(false);
      setOpportunityForm({
        title: '',
        prospect_id: '',
        client_id: '',
        description: '',
        estimated_value: '',
        stage: 'lead',
        probability: '20',
        expected_close_date: null,
        assigned_to: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateOpportunityStage = async (opportunityId, newStage) => {
    try {
      const probability = {
        lead: 10,
        qualified: 25,
        proposal: 50,
        negotiation: 75,
        won: 100,
        lost: 0,
      }[newStage];
      
      await api.put(`/opportunities/${opportunityId}`, { 
        stage: newStage,
        probability,
      });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // Calculate pipeline stats
  const pipelineValue = opportunities
    .filter(o => !['won', 'lost'].includes(o.stage))
    .reduce((sum, o) => sum + (o.estimated_value * o.probability / 100), 0);

  const wonValue = opportunities
    .filter(o => o.stage === 'won')
    .reduce((sum, o) => sum + o.estimated_value, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">CRM</h1>
            <p className="text-slate-500 mt-1">
              Gérez vos prospects et opportunités commerciales
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={isProspectDialogOpen} onOpenChange={setIsProspectDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-prospect-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Nouveau prospect
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouveau prospect</DialogTitle>
                  <DialogDescription>
                    Ajoutez un nouveau prospect à votre pipeline
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Entreprise *</Label>
                      <Input
                        value={prospectForm.company_name}
                        onChange={(e) => setProspectForm({ ...prospectForm, company_name: e.target.value })}
                        placeholder="Nom de l'entreprise"
                        data-testid="prospect-company-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact *</Label>
                      <Input
                        value={prospectForm.contact_name}
                        onChange={(e) => setProspectForm({ ...prospectForm, contact_name: e.target.value })}
                        placeholder="Nom du contact"
                        data-testid="prospect-contact-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={prospectForm.contact_email}
                        onChange={(e) => setProspectForm({ ...prospectForm, contact_email: e.target.value })}
                        placeholder="email@exemple.com"
                        data-testid="prospect-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={prospectForm.contact_phone}
                        onChange={(e) => setProspectForm({ ...prospectForm, contact_phone: e.target.value })}
                        placeholder="+33 1 23 45 67 89"
                        data-testid="prospect-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Select
                      value={prospectForm.source}
                      onValueChange={(v) => setProspectForm({ ...prospectForm, source: v })}
                    >
                      <SelectTrigger data-testid="prospect-source-select">
                        <SelectValue placeholder="Comment avez-vous trouvé ce prospect ?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Site web</SelectItem>
                        <SelectItem value="referral">Recommandation</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="event">Événement</SelectItem>
                        <SelectItem value="cold_call">Prospection</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={prospectForm.notes}
                      onChange={(e) => setProspectForm({ ...prospectForm, notes: e.target.value })}
                      placeholder="Informations complémentaires..."
                      data-testid="prospect-notes-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsProspectDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreateProspect} className="bg-slate-900 hover:bg-slate-800" data-testid="save-prospect-btn">
                    Créer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={isOpportunityDialogOpen} onOpenChange={setIsOpportunityDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-opportunity-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle opportunité
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Nouvelle opportunité</DialogTitle>
                  <DialogDescription>
                    Créez une nouvelle opportunité commerciale
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input
                      value={opportunityForm.title}
                      onChange={(e) => setOpportunityForm({ ...opportunityForm, title: e.target.value })}
                      placeholder="Ex: Mission développement - BNP"
                      data-testid="opportunity-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prospect</Label>
                      <Select
                        value={opportunityForm.prospect_id}
                        onValueChange={(v) => setOpportunityForm({ ...opportunityForm, prospect_id: v })}
                      >
                        <SelectTrigger data-testid="opportunity-prospect-select">
                          <SelectValue placeholder="Sélectionner un prospect" />
                        </SelectTrigger>
                        <SelectContent>
                          {prospects.filter(p => !p.is_converted).map((prospect) => (
                            <SelectItem key={prospect.prospect_id} value={prospect.prospect_id}>
                              {prospect.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Valeur estimée (€)</Label>
                      <Input
                        type="number"
                        value={opportunityForm.estimated_value}
                        onChange={(e) => setOpportunityForm({ ...opportunityForm, estimated_value: e.target.value })}
                        placeholder="50000"
                        data-testid="opportunity-value-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Étape</Label>
                      <Select
                        value={opportunityForm.stage}
                        onValueChange={(v) => setOpportunityForm({ ...opportunityForm, stage: v })}
                      >
                        <SelectTrigger data-testid="opportunity-stage-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.slice(0, 4).map((stage) => (
                            <SelectItem key={stage.value} value={stage.value}>
                              {stage.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Probabilité (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={opportunityForm.probability}
                        onChange={(e) => setOpportunityForm({ ...opportunityForm, probability: e.target.value })}
                        data-testid="opportunity-probability-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Date de clôture prévue</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="opportunity-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {opportunityForm.expected_close_date 
                            ? format(opportunityForm.expected_close_date, 'dd/MM/yyyy')
                            : 'Sélectionner une date'
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={opportunityForm.expected_close_date}
                          onSelect={(date) => setOpportunityForm({ ...opportunityForm, expected_close_date: date })}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={opportunityForm.description}
                      onChange={(e) => setOpportunityForm({ ...opportunityForm, description: e.target.value })}
                      placeholder="Détails de l'opportunité..."
                      data-testid="opportunity-description-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsOpportunityDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreateOpportunity} className="bg-slate-900 hover:bg-slate-800" data-testid="save-opportunity-btn">
                    Créer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{prospects.filter(p => !p.is_converted).length}</p>
                  <p className="text-sm text-slate-500">Prospects actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{opportunities.filter(o => !['won', 'lost'].includes(o.stage)).length}</p>
                  <p className="text-sm text-slate-500">Opportunités en cours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(pipelineValue)}</p>
                  <p className="text-sm text-slate-500">Pipeline pondéré</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(wonValue)}</p>
                  <p className="text-sm text-slate-500">Gagné</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="pipeline-tab">Pipeline</TabsTrigger>
            <TabsTrigger value="prospects" data-testid="prospects-tab">Prospects</TabsTrigger>
          </TabsList>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {STAGES.map((stage) => {
                const stageOpportunities = opportunities.filter(o => o.stage === stage.value);
                const stageValue = stageOpportunities.reduce((sum, o) => sum + o.estimated_value, 0);
                
                return (
                  <div key={stage.value} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge className={stage.color}>{stage.label}</Badge>
                      <span className="text-sm text-slate-500">{stageOpportunities.length}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-600">{formatCurrency(stageValue)}</p>
                    
                    <div className="space-y-2 min-h-[200px]">
                      {stageOpportunities.map((opp) => (
                        <Card 
                          key={opp.opportunity_id} 
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          data-testid={`opportunity-card-${opp.opportunity_id}`}
                        >
                          <CardContent className="p-3">
                            <h4 className="font-medium text-sm text-slate-900 mb-1">{opp.title}</h4>
                            <p className="text-xs text-slate-500 mb-2">
                              {opp.prospect_name || opp.client_name || 'Non assigné'}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-700">
                                {formatCurrency(opp.estimated_value)}
                              </span>
                              <span className="text-xs text-slate-400">{opp.probability}%</span>
                            </div>
                            {/* Stage navigation buttons */}
                            {!['won', 'lost'].includes(stage.value) && (
                              <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100">
                                {stage.value !== 'lead' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs h-6 px-2"
                                    onClick={() => {
                                      const currentIndex = STAGES.findIndex(s => s.value === stage.value);
                                      handleUpdateOpportunityStage(opp.opportunity_id, STAGES[currentIndex - 1].value);
                                    }}
                                  >
                                    ←
                                  </Button>
                                )}
                                {stage.value !== 'negotiation' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs h-6 px-2 ml-auto"
                                    onClick={() => {
                                      const currentIndex = STAGES.findIndex(s => s.value === stage.value);
                                      handleUpdateOpportunityStage(opp.opportunity_id, STAGES[currentIndex + 1].value);
                                    }}
                                  >
                                    →
                                  </Button>
                                )}
                                {stage.value === 'negotiation' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs h-6 px-2 text-emerald-600 ml-auto"
                                      onClick={() => handleUpdateOpportunityStage(opp.opportunity_id, 'won')}
                                    >
                                      Gagné
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs h-6 px-2 text-red-600"
                                      onClick={() => handleUpdateOpportunityStage(opp.opportunity_id, 'lost')}
                                    >
                                      Perdu
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Prospects Tab */}
          <TabsContent value="prospects" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prospects.filter(p => !p.is_converted).map((prospect) => (
                <Card key={prospect.prospect_id} className="card-hover" data-testid={`prospect-card-${prospect.prospect_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{prospect.company_name}</h3>
                        <p className="text-sm text-slate-500">{prospect.contact_name}</p>
                      </div>
                      {prospect.source && (
                        <Badge variant="secondary" className="text-xs">
                          {prospect.source}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {prospect.contact_email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4" />
                          <span>{prospect.contact_email}</span>
                        </div>
                      )}
                      {prospect.contact_phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Phone className="w-4 h-4" />
                          <span>{prospect.contact_phone}</span>
                        </div>
                      )}
                    </div>

                    {prospect.notes && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{prospect.notes}</p>
                    )}

                    <Button
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      onClick={() => handleConvertProspect(prospect.prospect_id)}
                      data-testid={`convert-prospect-${prospect.prospect_id}`}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Convertir en client
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {prospects.filter(p => !p.is_converted).length === 0 && (
                <div className="col-span-full">
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center">
                        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Aucun prospect pour le moment</p>
                        <p className="text-sm text-slate-400 mt-1">
                          Ajoutez votre premier prospect pour commencer
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
